import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Save, FileText, Eye, Edit3, Shield, Scale, RotateCcw, Cookie } from "lucide-react";
import { renderMarkdownSafe } from "../../lib/safeMarkdown";

const SECTIONS = [
  { slug: "privacy", label: "Privacy Policy", icon: Shield, public: "/privacy" },
  { slug: "terms_of_service", label: "Conditions of Service", icon: Scale, public: "/legal/terms" },
  { slug: "cancellation_policy", label: "Cancellation Policy", icon: RotateCcw, public: "/legal/cancellation" },
  { slug: "cookie_policy", label: "Cookie Policy", icon: Cookie, public: "/legal/cookies" }
];

export default function LegalTab() {
  const [active, setActive] = useState("privacy");
  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(SECTIONS.map(s => axios.get(`${API}/legal/${s.slug}`)));
      const map = {};
      results.forEach((r, i) => { map[SECTIONS[i].slug] = r.data; });
      setDocs(map);
    } catch (e) {
      console.error("Failed to load legal pages", e);
      toast.error("Failed to load legal pages");
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setContent = (slug, content) => setDocs(p => ({ ...p, [slug]: { ...(p[slug] || {}), content_md: content } }));

  const save = async () => {
    const content = docs[active]?.content_md || "";
    if (!content.trim()) { toast.error("Content cannot be empty"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/legal/${active}`, { content_md: content });
      toast.success(`${SECTIONS.find(s => s.slug === active).label} saved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const currentSlug = active;
  const currentContent = docs[currentSlug]?.content_md || "";
  const previewHtml = useMemo(
    () => ({ __html: renderMarkdownSafe(currentContent, "admin-preview") }),
    [currentContent]
  );

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  const current = docs[active] || {};

  return (
    <div data-testid="legal-tab" className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#C5A059]" /> Legal Pages
        </h2>
        <p className="text-white/40 text-xs mt-1">Markdown supported. Each page is publicly accessible.</p>
      </div>

      <div className="flex gap-1 border-b border-white/5">
        {SECTIONS.map(s => (
          <button key={s.slug} onClick={() => { setActive(s.slug); setShowPreview(false); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm transition-colors border-b-2 ${active === s.slug ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-white/50 hover:text-white"}`}
            data-testid={`legal-section-${s.slug}`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-white/40 text-xs">
          {current.updated_at && <span>Last updated: {current.updated_at.slice(0, 16).replace("T", " ")}</span>}
        </p>
        <div className="flex gap-2">
          <Button onClick={() => setShowPreview(!showPreview)} variant="ghost" className="text-white/60">
            {showPreview ? <><Edit3 className="w-4 h-4 mr-2" /> Edit</> : <><Eye className="w-4 h-4 mr-2" /> Preview</>}
          </Button>
          <Button onClick={save} disabled={saving} className="btn-gold" data-testid={`save-legal-${active}`}>
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {showPreview ? (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-6 min-h-[400px] text-white/80"
          dangerouslySetInnerHTML={previewHtml} />
      ) : (
        <textarea
          value={current.content_md || ""}
          onChange={(e) => setContent(active, e.target.value)}
          className="w-full h-[600px] bg-[#0A0A0A] border border-[#333] rounded-sm p-4 text-white/80 text-sm font-mono resize-y focus:border-[#C5A059]/50 focus:outline-none"
          spellCheck="false"
          data-testid={`legal-editor-${active}`}
        />
      )}
    </div>
  );
}
